import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Package, FileText, Cog, TrendingUp, AlertCircle, Play, CheckCircle, LogOut, User, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { canViewModule, hasPermission, getUser, getUserRole, isAdmin } from '@/utils/permissions';
import { getSessionItem, clearSession, getCurrentUser, getSessionToken, getCurrentPlantId, setCurrentPlantId, getPlants } from '@/utils/sessionManager';
import ScheduleUpload from './ScheduleUpload';
import CustomerScheduleView from './CustomerScheduleView';
import DailyDemandView from './DailyDemandView';
import MrpView from './MrpView';
import ProcurementView from './ProcurementView';
import ProductionView from './ProductionView';
import ProductionExecutionView from './ProductionExecutionView';
import MastersManagement from './MastersManagement';
import InventoryView from './InventoryView';
import VendorShipmentManagement from './VendorShipmentManagement';
import GrnView from './GrnView';
import DispatchView from './DispatchView';
import TransactionManagement from './TransactionManagement';
import UserManagement from './UserManagement';
import ShortfallHistory from './ShortfallHistory';
import ParkedInventoryManagement from './ParkedInventoryManagement';
import EmailAlertSettings from './EmailAlertSettings';
import SystemSettings from './SystemSettings';
import OrderTrackingDashboard from './OrderTrackingDashboard';
import AnalyticsDashboard from './AnalyticsDashboard';
import CustomerReturns from './CustomerReturns';
import ReworkProduction from './ReworkProduction';
import AuditLog from './AuditLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlannerDashboard = () => {
  const [kpis, setKpis] = useState({
    total_schedules: 0,
    total_daily_demands: 0,
    total_mrp_requirements: 0,
    total_pos: 0,
    total_production_orders: 0,
    fg_items: 0,
    rm_items: 0,
    sfg_items: 0
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [userPermissions, setUserPermissions] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [plants, setPlants] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(getCurrentPlantId() || '');

  useEffect(() => {
    // Load user and permissions
    const user = getUser();
    const role = getUserRole();
    setCurrentUser(user);
    setUserPermissions(role ? role.permissions : {});
    
    // Load plants - filter by user's accessible_plants
    const userAccessiblePlants = user?.accessible_plants || [];
    const storedPlants = getPlants();
    const filterByAccess = (plantsList) => {
      if (!userAccessiblePlants.length) return plantsList;
      return plantsList.filter(p => userAccessiblePlants.includes(p.id));
    };
    
    if (storedPlants.length > 0) {
      setPlants(filterByAccess(storedPlants));
    } else {
      // Fetch plants from API
      axios.get(`${API}/plants`).then(res => {
        const plantsList = res.data || [];
        setPlants(filterByAccess(plantsList));
      }).catch(() => {});
    }
    
    // Set default plant if not set
    if (!getCurrentPlantId() && user?.plant_id) {
      setCurrentPlantId(user.plant_id);
      setSelectedPlant(user.plant_id);
    }
  }, []);

  const handlePlantChange = useCallback((plantId) => {
    setSelectedPlant(plantId);
    setCurrentPlantId(plantId);
    // Reload the page to refresh all data with new plant context
    window.location.reload();
  }, []);

  const handleLogout = async () => {
    try {
      // Get user info for API call from sessionStorage
      const user = getCurrentUser();
      const sessionToken = getSessionToken();
      
      if (user) {
        // Call backend to clear session token
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            user_id: user.id,
            session_token: sessionToken
          })
        });
      }
    } catch (error) {
      console.error('Logout API error:', error);
    }
    
    // Clear all session data
    clearSession();
    
    // Show success message
    toast.success('Logged out successfully');
    
    // Redirect to login page
    window.location.href = '/login';
  };

  useEffect(() => {
    fetchKpis();
  }, []);

  const fetchKpis = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/kpis`);
      setKpis(response.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    }
  };

  const handleRunMRP = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/mrp/run`);
      toast.success(`MRP completed: ${response.data.requirements_created} requirements created`);
      fetchKpis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'MRP run failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePOs = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/procurement/generate-pos`);
      
      // Show detailed message about protected MROs
      if (response.data.protected_mros > 0) {
        toast.success(
          `✅ Generated ${response.data.pos_created} new MROs | 🔒 Protected ${response.data.protected_mros} MROs with shipments`,
          { duration: 6000 }
        );
      } else {
        toast.success(`MRO Generation completed: ${response.data.pos_created} MROs created`);
      }
      
      fetchKpis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'MRO generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleProductionPlan = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/production/plan`);
      toast.success(`Production Planning completed: ${response.data.production_orders_created} work orders created`);
      fetchKpis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Production planning failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRunSafetyStockCheck = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/procurement/safety-stock-replenishment`);
      
      if (response.data.pos && response.data.pos.length > 0) {
        const created = response.data.pos.filter(p => p.action === 'created').length;
        const updated = response.data.pos.filter(p => p.action === 'updated').length;
        
        toast.success(
          `Safety Stock Check Complete: ${created} MRO(s) created, ${updated} MRO(s) updated`,
          { duration: 6000 }
        );
      } else {
        toast.info('Safety Stock Check Complete: All items are at safe stock levels');
      }
      
      fetchKpis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Safety stock check failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <Cog className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Mini-ERP Planner</h1>
                <p className="text-sm text-slate-500">Manufacturing Planning & Control</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Plant Switcher */}
              {plants.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg" data-testid="plant-switcher">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <Select value={selectedPlant} onValueChange={handlePlantChange}>
                    <SelectTrigger className="w-[160px] h-8 border-0 bg-transparent text-sm font-medium text-blue-800 focus:ring-0 p-0" data-testid="plant-select-trigger">
                      <SelectValue placeholder="Select Plant" />
                    </SelectTrigger>
                    <SelectContent>
                      {isAdmin() && (
                        <SelectItem value="all" data-testid="plant-option-all">All Plants</SelectItem>
                      )}
                      {plants.map(p => (
                        <SelectItem key={p.id} value={p.id} data-testid={`plant-option-${p.id}`}>
                          {p.code ? `${p.code} - ${p.name}` : p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {currentUser && (
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
                  <User className="w-4 h-4 text-slate-600" />
                  <div className="text-sm">
                    <p className="font-medium text-slate-800">{currentUser.username}</p>
                    <p className="text-xs text-slate-500">{currentUser.role_name || 'User'}</p>
                  </div>
                </div>
              )}
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
              
              {/* Admin Action Buttons - Only visible with proper permissions */}
              {hasPermission('mrp', 'create') && (
                <Button
                  data-testid="run-mrp-btn"
                  onClick={handleRunMRP}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Run MRP
                </Button>
              )}
              
              {hasPermission('procurement', 'create') && (
                <Button
                  data-testid="generate-pos-btn"
                  onClick={handleGeneratePOs}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Generate MROs
                </Button>
              )}
              
              {hasPermission('production', 'create') && (
                <Button
                  data-testid="production-plan-btn"
                  onClick={handleProductionPlan}
                  disabled={loading}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Plan Production
                </Button>
              )}
              
              {hasPermission('procurement', 'create') && (
                <Button
                  data-testid="safety-stock-btn"
                  onClick={handleRunSafetyStockCheck}
                  disabled={loading}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  title="Run safety stock check and generate replenishment MROs"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Safety Stock
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card data-testid="kpi-schedules" className="border-l-4 border-l-blue-500 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardDescription className="text-sm font-medium text-slate-600">Monthly Schedules</CardDescription>
              <CardTitle className="text-3xl font-bold text-slate-900">{kpis.total_schedules}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center text-sm text-slate-500">
                <Calendar className="w-4 h-4 mr-2" />
                Uploaded schedules
              </div>
            </CardContent>
          </Card>

          <Card data-testid="kpi-daily-demands" className="border-l-4 border-l-green-500 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardDescription className="text-sm font-medium text-slate-600">Daily Demands</CardDescription>
              <CardTitle className="text-3xl font-bold text-slate-900">{kpis.total_daily_demands}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center text-sm text-slate-500">
                <TrendingUp className="w-4 h-4 mr-2" />
                Active demand lines
              </div>
            </CardContent>
          </Card>

          <Card data-testid="kpi-mrp" className="border-l-4 border-l-orange-500 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardDescription className="text-sm font-medium text-slate-600">MRP Requirements</CardDescription>
              <CardTitle className="text-3xl font-bold text-slate-900">{kpis.total_mrp_requirements}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center text-sm text-slate-500">
                <AlertCircle className="w-4 h-4 mr-2" />
                Material requirements
              </div>
            </CardContent>
          </Card>

          <Card data-testid="kpi-pos" className="border-l-4 border-l-purple-500 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardDescription className="text-sm font-medium text-slate-600">Milk Run Orders</CardDescription>
              <CardTitle className="text-3xl font-bold text-slate-900">{kpis.total_pos}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center text-sm text-slate-500">
                <FileText className="w-4 h-4 mr-2" />
                Generated POs
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card data-testid="kpi-wos" className="shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Work Orders</p>
                  <p className="text-2xl font-bold text-slate-900">{kpis.total_production_orders}</p>
                </div>
                <Package className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="kpi-fg-items" className="shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">FG Items</p>
                  <p className="text-2xl font-bold text-slate-900">{kpis.fg_items}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="kpi-sfg-items" className="shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">SFG Items</p>
                  <p className="text-2xl font-bold text-slate-900">{kpis.sfg_items}</p>
                </div>
                <Package className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="kpi-rm-items" className="shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">RM Items</p>
                  <p className="text-2xl font-bold text-slate-900">{kpis.rm_items}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Section */}
        <Card data-testid="main-tabs-card" className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Planning Modules</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full mb-6 h-auto flex-wrap gap-1 justify-start">
                <TabsTrigger data-testid="tab-overview" value="overview" className="text-xs px-3 py-2">Overview</TabsTrigger>
                {canViewModule('schedule') && <TabsTrigger data-testid="tab-schedule" value="schedule" className="text-xs px-3 py-2">Schedule</TabsTrigger>}
                {canViewModule('demand') && <TabsTrigger data-testid="tab-demand" value="demand" className="text-xs px-3 py-2">Demand</TabsTrigger>}
                {canViewModule('mrp') && <TabsTrigger data-testid="tab-mrp" value="mrp" className="text-xs px-3 py-2">MRP</TabsTrigger>}
                {canViewModule('procurement') && <TabsTrigger data-testid="tab-procurement" value="procurement" className="text-xs px-3 py-2">Milk Run Orders</TabsTrigger>}
                {canViewModule('shipment') && <TabsTrigger data-testid="tab-shipment" value="shipment" className="text-xs px-3 py-2">Shipment</TabsTrigger>}
                {canViewModule('grn') && <TabsTrigger data-testid="tab-grn" value="grn" className="text-xs px-3 py-2">GRN</TabsTrigger>}
                {canViewModule('production') && <TabsTrigger data-testid="tab-production" value="production" className="text-xs px-3 py-2">Production</TabsTrigger>}
                {canViewModule('dispatch') && <TabsTrigger data-testid="tab-dispatch" value="dispatch" className="text-xs px-3 py-2">Dispatch</TabsTrigger>}
                {canViewModule('customer_returns') && <TabsTrigger data-testid="tab-customer-returns" value="customer-returns" className="text-xs px-3 py-2">Returns</TabsTrigger>}
                {canViewModule('rework') && <TabsTrigger data-testid="tab-rework-production" value="rework-production" className="text-xs px-3 py-2">Rework</TabsTrigger>}
                {canViewModule('inventory') && <TabsTrigger data-testid="tab-inventory" value="inventory" className="text-xs px-3 py-2">Inventory</TabsTrigger>}
                {canViewModule('transactions') && <TabsTrigger data-testid="tab-transactions" value="transactions" className="text-xs px-3 py-2">Transactions</TabsTrigger>}
                {canViewModule('order_tracking') && <TabsTrigger data-testid="tab-tracking" value="tracking" className="text-xs px-3 py-2">Order Tracking</TabsTrigger>}
                {canViewModule('analytics') && <TabsTrigger data-testid="tab-analytics" value="analytics" className="text-xs px-3 py-2">Analytics</TabsTrigger>}
                {canViewModule('procurement') && <TabsTrigger data-testid="tab-shortfall" value="shortfall" className="text-xs px-3 py-2">Vendor Ledger</TabsTrigger>}
                {isAdmin() && <TabsTrigger data-testid="tab-users" value="users" className="text-xs px-3 py-2">Users</TabsTrigger>}
                {isAdmin() && <TabsTrigger data-testid="tab-system-settings" value="system-settings" className="text-xs px-3 py-2">System Settings</TabsTrigger>}
                {isAdmin() && <TabsTrigger data-testid="tab-email" value="email" className="text-xs px-3 py-2">Email Alerts</TabsTrigger>}
                {isAdmin() && <TabsTrigger data-testid="tab-audit-log" value="audit-log" className="text-xs px-3 py-2">Audit Log</TabsTrigger>}
                {canViewModule('masters') && <TabsTrigger data-testid="tab-masters" value="masters" className="text-xs px-3 py-2">Masters</TabsTrigger>}
              </TabsList>

              <TabsContent value="overview" data-testid="tab-content-overview">
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">Workflow Overview</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                        <div>
                          <p className="font-medium text-slate-800">Upload Monthly Schedule</p>
                          <p className="text-sm text-slate-600">Import customer demand and auto-split to daily based on working calendar</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                        <div>
                          <p className="font-medium text-slate-800">Run MRP (BOM Explosion)</p>
                          <p className="text-sm text-slate-600">Explode BOMs to calculate material requirements with lead times</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                        <div>
                          <p className="font-medium text-slate-800">Generate Milk Run Orders</p>
                          <p className="text-sm text-slate-600">Allocate quantities by vendor SOB% and create MROs with correct lead times</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                        <div>
                          <p className="font-medium text-slate-800">Plan Production</p>
                          <p className="text-sm text-slate-600">Create work orders for in-house items with machine and skill assignments</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="schedule" data-testid="tab-content-schedule">
                <CustomerScheduleView />
              </TabsContent>

              <TabsContent value="demand" data-testid="tab-content-demand">
                <DailyDemandView />
              </TabsContent>

              <TabsContent value="mrp" data-testid="tab-content-mrp">
                <MrpView />
              </TabsContent>

              <TabsContent value="procurement" data-testid="tab-content-procurement">
                <ProcurementView />
              </TabsContent>

              <TabsContent value="shipment" data-testid="tab-content-shipment">
                <VendorShipmentManagement userPermissions={userPermissions} />
              </TabsContent>

              <TabsContent value="grn" data-testid="tab-content-grn">
                <GrnView />
              </TabsContent>

              <TabsContent value="production" data-testid="tab-content-production">
                <ProductionExecutionView />
              </TabsContent>

              <TabsContent value="dispatch" data-testid="tab-content-dispatch">
                <DispatchView />
              </TabsContent>

              <TabsContent value="customer-returns" data-testid="tab-content-customer-returns">
                <CustomerReturns />
              </TabsContent>

              <TabsContent value="rework-production" data-testid="tab-content-rework-production">
                <ReworkProduction />
              </TabsContent>

              <TabsContent value="inventory" data-testid="tab-content-inventory">
                <Tabs defaultValue="available" className="space-y-4">
                  <TabsList>
                    <TabsTrigger value="available">Inventory Levels</TabsTrigger>
                    <TabsTrigger value="parked">Parked Inventory</TabsTrigger>
                  </TabsList>
                  <TabsContent value="available">
                    <InventoryView />
                  </TabsContent>
                  <TabsContent value="parked">
                    <ParkedInventoryManagement />
                  </TabsContent>
                </Tabs>
              </TabsContent>

              <TabsContent value="transactions" data-testid="tab-content-transactions">
                <TransactionManagement />
              </TabsContent>

              <TabsContent value="tracking" data-testid="tab-content-tracking">
                <OrderTrackingDashboard />
              </TabsContent>

              <TabsContent value="analytics" data-testid="tab-content-analytics">
                <AnalyticsDashboard />
              </TabsContent>

              <TabsContent value="shortfall" data-testid="tab-content-shortfall">
                <ShortfallHistory />
              </TabsContent>

              <TabsContent value="users" data-testid="tab-content-users">
                <UserManagement />
              </TabsContent>

              <TabsContent value="system-settings" data-testid="tab-content-system-settings">
                <SystemSettings />
              </TabsContent>

              <TabsContent value="email" data-testid="tab-content-email">
                <EmailAlertSettings />
              </TabsContent>

              <TabsContent value="audit-log" data-testid="tab-content-audit-log">
                <AuditLog />
              </TabsContent>

              <TabsContent value="masters" data-testid="tab-content-masters">
                <MastersManagement />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PlannerDashboard;
